import React, { useState, useEffect } from 'react';
import { ShieldOff, AlertTriangle } from 'lucide-react';
import { tracciabilitaAPI } from '../../services/tracciabilitaApi';
import '../../styles/SomministrazioniVisita.css';
import '../../styles/SomministrazioniVisitaConsole.css';

/**
 * SomministrazioniVisita — cosa è uscito dal magazzino per questa procedura.
 *
 * COSA MOSTRA E A CHI
 * Il filtro non è qui: lo applica il server, che conosce chi sta
 * chiedendo. Di una procedura riservata, chi non l'ha eseguita riceve un
 * elenco vuoto e un avviso — prodotti e quantità stanno al livello «su
 * richiesta» della riservatezza estetica, insieme alle fotografie e alle
 * note. Ripetere la regola anche qui vorrebbe dire due punti dove
 * modificarla, e prima o poi se ne modifica uno solo.
 *
 * IL LOTTO NON SI INDICA DA QUI
 * Lo registra la segreteria dalla coda del magazzino, leggendolo dal
 * tagliando che il medico le consegna. Qui si dice soltanto che manca:
 * un campo per digitarlo in cartella rimetterebbe la stessa cosa in due
 * posti diversi, e la riga di magazzino la smaltirebbe chi arriva primo.
 */

const dataIta = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : d.toLocaleDateString('it-IT');
};

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n.toLocaleString('it-IT', { maximumFractionDigits: 3 }) : '—';
};

const SomministrazioniVisita = ({ visitaId, pazienteId }) => {
    const [righe, setRighe] = useState([]);
    const [nascosta, setNascosta] = useState(false);
    const [avviso, setAvviso] = useState(null);
    const [caricamento, setCaricamento] = useState(true);
    const [inAttesa, setInAttesa] = useState(0);

    const carica = React.useCallback(async () => {
        if (!visitaId) return;
        try {
            setCaricamento(true);
            const r = await tracciabilitaAPI.perVisita(visitaId);
            setRighe(r.data.data.somministrazioni || []);
            setNascosta(Boolean(r.data.data.riservata_nascosta));
            setAvviso(r.data.message || null);
            try {
                const d = await tracciabilitaAPI.daIndicare(visitaId);
                setInAttesa((d.data.data.da_indicare || []).length);
            } catch (e) { /* se non si sa, non si dice niente */ }
        } catch (e) {
            setAvviso(e?.response?.data?.message || 'Non riesco a leggere i prodotti');
        } finally {
            setCaricamento(false);
        }
    }, [visitaId]);

    useEffect(() => { carica(); }, [carica]);

    if (caricamento) return <p className="sv-attesa">Caricamento…</p>;

    if (nascosta) {
        return (
            <p className="sv-riservata">
                <ShieldOff size={15} />
                {avviso || 'Procedura riservata: i prodotti si vedono aprendo il dettaglio.'}
            </p>
        );
    }

    return (
        <>
            {righe.length === 0 ? (
                <p className="sv-vuoto">Nessun prodotto scaricato per questa procedura.</p>
            ) : (
                <table className="sv-tabella">
                    <thead>
                        <tr>
                            <th>Prodotto</th>
                            <th className="num">Q.tà</th>
                            <th>Lotto</th>
                            <th>Scadenza</th>
                        </tr>
                    </thead>
                    <tbody>
                        {righe.map((r) => (
                            <tr key={r.movimento_id}>
                                <td>
                                    {r.prodotto}
                                    {r.tracciabilita_rigorosa && (
                                        <span className="sv-sub">lotto obbligatorio</span>
                                    )}
                                </td>
                                <td className="num">{num(r.quantita)} {r.unita_misura}</td>
                                <td className="sv-lotto">
                                    {r.codice_lotto || '—'}
                                    {r.da_regolarizzare && (
                                        <span className="sv-avviso" title="Lotto registrato all'uso, non ancora caricato a magazzino">
                                            <AlertTriangle size={12} /> da regolarizzare
                                        </span>
                                    )}
                                </td>
                                <td>{dataIta(r.scadenza)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {inAttesa > 0 && (
                <p className="sv-in-coda">
                    <AlertTriangle size={14} />
                    {inAttesa === 1
                        ? 'Un prodotto a lotto obbligatorio è in attesa di scarico'
                        : `${inAttesa} prodotti a lotto obbligatorio sono in attesa di scarico`}
                    : lo registra la segreteria dalla coda del magazzino, col tagliando
                    consegnato dal medico.
                </p>
            )}
        </>
    );
};

export default SomministrazioniVisita;
